import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import {
  ArrowLeft, UserCircle, CalendarDays, ShieldCheck, TrendingUp, Heart,
  StickyNote, FileText, Save, Edit3, X, ChevronRight,
} from "lucide-react";
import { formatApiError } from "../utils/apiError";
import { useTruthState } from "../hooks/useTruthState";
import { TruthReceipt } from "../components/TruthReceipt";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PassportProfile — learner's structured Driving Passport.
 * 7 sections, mobile-first, inline edit, shared structure across every learner.
 * Learner owns the data. Instructor notes appear read-only here.
 */
export default function PassportProfile() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [data, setData] = useState(null);
  const [openSection, setOpenSection] = useState("identity");
  const truth = useTruthState();

  const load = async () => {
    try {
      await truth.run(
        async () => {
          const { data } = await axios.get(`${API}/api/passport/profile`, { withCredentials: true });
          setData(data);
        },
        { failedMessage: "Couldn't load your passport." },
      );
    } catch { /* truth.run captured */ }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);

  if (!data) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 flex items-center justify-center px-4">
        <div className="w-full max-w-sm space-y-3">
          <p className="text-slate-400 text-center">Loading your passport…</p>
          <TruthReceipt state={truth.state} message={truth.message} onRetry={load} onDismiss={() => truth.setState(null)} />
        </div>
      </div>
    );
  }
  const s = data.sections;

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-20">
      <header className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="passport-profile-back"
            onClick={() => goBack()}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-extrabold text-slate-900 dark:text-white">Driving Passport</h1>
            <p className="text-[11px] text-slate-500">Your progress, your data — portable across instructors</p>
          </div>
          <button
            data-testid="passport-transfer-view"
            onClick={() => nav("/passport/transfer-view")}
            className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 flex items-center gap-1"
          >
            <FileText className="w-3 h-3" /> Transfer view
          </button>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
        <TruthReceipt state={truth.state} message={truth.message} onRetry={load} onDismiss={() => truth.setState(null)} />

        <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/15 dark:to-indigo-900/15 border border-blue-200 dark:border-blue-900/40 p-3">
          <p className="text-[11px] text-blue-900 dark:text-blue-200 leading-snug">
            <span className="font-extrabold">Learner progress belongs to the learner.</span> Fill in what you want, share only what you want. Your instructor's notes are read-only here.
          </p>
        </div>

        <IdentitySection
          truth={truth}
          open={openSection === "identity"}
          onToggle={() => setOpenSection(openSection === "identity" ? null : "identity")}
          initial={s.identity}
          onSaved={(next) => setData({ ...data, sections: { ...data.sections, identity: next } })}
        />
        <AvailabilitySection
          truth={truth}
          open={openSection === "availability"}
          onToggle={() => setOpenSection(openSection === "availability" ? null : "availability")}
          initial={s.availability}
          onSaved={(next) => setData({ ...data, sections: { ...data.sections, availability: next } })}
        />
        <ReadinessSection
          truth={truth}
          open={openSection === "readiness"}
          onToggle={() => setOpenSection(openSection === "readiness" ? null : "readiness")}
          initial={s.readiness}
          onSaved={(next) => setData({ ...data, sections: { ...data.sections, readiness: next } })}
        />
        <ProgressSection
          open={openSection === "progress"}
          onToggle={() => setOpenSection(openSection === "progress" ? null : "progress")}
          data={s.progress}
        />
        <ConfidenceSection
          truth={truth}
          open={openSection === "confidence"}
          onToggle={() => setOpenSection(openSection === "confidence" ? null : "confidence")}
          initial={s.confidence}
          onSaved={(next) => setData({ ...data, sections: { ...data.sections, confidence: next } })}
        />
        <NotesSection
          open={openSection === "notes"}
          onToggle={() => setOpenSection(openSection === "notes" ? null : "notes")}
          data={s.instructor_notes}
        />
      </div>
    </div>
  );
}

/* ───────── Section frame ───────── */
function SectionCard({ testid, open, onToggle, icon, title, subtitle, children }) {
  return (
    <section data-testid={testid} className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center gap-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/30"
      >
        <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-extrabold text-slate-900 dark:text-white">{title}</p>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{subtitle}</p>
        </div>
        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4 pt-0 border-t border-slate-100 dark:border-slate-700">{children}</div>}
    </section>
  );
}

/* ───────── 1. Identity ───────── */
function IdentitySection({ truth, open, onToggle, initial, onSaved }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(initial); }, [initial]);

  const save = async () => {
    setSaving(true);
    try {
      await truth.run(
        async () => {
          const { data } = await axios.put(`${API}/api/passport/profile/identity`, {
            phone: f.phone, postcode: f.postcode, transmission: f.transmission,
          }, { withCredentials: true });
          onSaved(data.sections.identity);
          onToggle();
        },
        {
          completedMessage: "Identity saved.",
          failedMessage:    formatApiError(null, "Couldn't save."),
        },
      );
    } catch { /* truth.run captured */ }
    setSaving(false);
  };

  const subtitle = [f.postcode, f.transmission && f.transmission !== "either" ? `${f.transmission} transmission` : null]
    .filter(Boolean).join(" · ") || "Tap to add your details";

  return (
    <SectionCard testid="section-identity" open={open} onToggle={onToggle}
      icon={<UserCircle className="w-5 h-5 text-blue-600" />}
      title="Identity"
      subtitle={subtitle}>
      <div className="mt-3 space-y-3">
        <Readonly label="Name" value={f.name} />
        <Readonly label="Email" value={f.email} small />
        <Input testid="identity-phone" label="Phone" value={f.phone || ""} onChange={(v) => setF({ ...f, phone: v })} placeholder="07700 900000" />
        <Input testid="identity-postcode" label="Postcode" value={f.postcode || ""} onChange={(v) => setF({ ...f, postcode: v.toUpperCase() })} placeholder="RG1 2BQ" />
        <div>
          <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">Transmission</label>
          <div className="mt-1 flex gap-2">
            {["manual", "auto", "either"].map((opt) => (
              <button
                key={opt}
                data-testid={`identity-transmission-${opt}`}
                onClick={() => setF({ ...f, transmission: opt })}
                className={`flex-1 px-2 py-1.5 text-xs font-bold rounded-lg border ${
                  f.transmission === opt ? "bg-blue-600 text-white border-blue-600" : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700"
                }`}
              >{opt === "either" ? "Either" : opt[0].toUpperCase() + opt.slice(1)}</button>
            ))}
          </div>
        </div>
        <button data-testid="identity-save" onClick={save} disabled={saving}
          className="w-full px-4 py-2.5 text-sm font-extrabold rounded-xl bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 flex items-center justify-center gap-2">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </SectionCard>
  );
}

/* ───────── 2. Availability ───────── */
function AvailabilitySection({ truth, open, onToggle, initial, onSaved }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(initial); }, [initial]);

  const toggleDay = (d) => {
    const arr = new Set(f.preferred_days || []);
    arr.has(d) ? arr.delete(d) : arr.add(d);
    setF({ ...f, preferred_days: Array.from(arr) });
  };
  const toggleTime = (t) => {
    const arr = new Set(f.preferred_times || []);
    arr.has(t) ? arr.delete(t) : arr.add(t);
    setF({ ...f, preferred_times: Array.from(arr) });
  };
  const save = async () => {
    setSaving(true);
    try {
      await truth.run(
        async () => {
          const { data } = await axios.put(`${API}/api/passport/profile/availability`, f, { withCredentials: true });
          onSaved(data.sections.availability);
          onToggle();
        },
        {
          completedMessage: "Availability saved.",
          failedMessage:    formatApiError(null, "Couldn't save."),
        },
      );
    } catch { /* truth.run captured */ }
    setSaving(false);
  };

  const subtitle = (f.preferred_days?.length || 0) > 0
    ? `${f.preferred_days.join(" · ")}${f.preferred_times?.length ? " · " + f.preferred_times.join(", ") : ""}`
    : "Tap to set when you're free";

  return (
    <SectionCard testid="section-availability" open={open} onToggle={onToggle}
      icon={<CalendarDays className="w-5 h-5 text-emerald-600" />}
      title="Availability"
      subtitle={subtitle}>
      <div className="mt-3 space-y-3">
        <div>
          <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">Preferred days</label>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <button key={d}
                data-testid={`availability-day-${d}`}
                onClick={() => toggleDay(d)}
                className={`py-2 text-[11px] font-bold rounded-lg border ${
                  (f.preferred_days || []).includes(d)
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700"
                }`}
              >{d}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">Preferred times</label>
          <div className="mt-1 grid grid-cols-2 gap-2">
            {["morning", "afternoon", "evening", "weekend"].map((t) => (
              <button key={t}
                data-testid={`availability-time-${t}`}
                onClick={() => toggleTime(t)}
                className={`py-2 text-xs font-bold rounded-lg border capitalize ${
                  (f.preferred_times || []).includes(t)
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700"
                }`}
              >{t}</button>
            ))}
          </div>
        </div>
        <Textarea testid="availability-pickup" label="Pickup notes" value={f.pickup_notes || ""} onChange={(v) => setF({ ...f, pickup_notes: v })} placeholder="e.g. Red gate, park on the side road" />
        <button data-testid="availability-save" onClick={save} disabled={saving}
          className="w-full px-4 py-2.5 text-sm font-extrabold rounded-xl bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 flex items-center justify-center gap-2">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </SectionCard>
  );
}

/* ───────── 3. Readiness ───────── */
function ReadinessSection({ truth, open, onToggle, initial, onSaved }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(initial); }, [initial]);

  const save = async () => {
    setSaving(true);
    try {
      await truth.run(
        async () => {
          const payload = {
            theory_passed: !!f.theory_passed,
            theory_passed_date: f.theory_passed_date || null,
            test_booked_date: f.test_booked_date || null,
            test_centre: f.test_centre || null,
          };
          const { data } = await axios.put(`${API}/api/passport/profile/readiness`, payload, { withCredentials: true });
          onSaved(data.sections.readiness);
          onToggle();
        },
        {
          completedMessage: "Readiness saved.",
          failedMessage:    formatApiError(null, "Couldn't save."),
        },
      );
    } catch { /* truth.run captured */ }
    setSaving(false);
  };

  const statusDot = {
    verified: "bg-emerald-500",
    pending: "bg-amber-500",
    expired: "bg-red-500",
    none: "bg-slate-300",
  }[f.licence_check_status] || "bg-slate-300";

  const subtitle = [
    f.theory_passed ? "Theory passed" : "Theory pending",
    f.test_booked_date ? `Test ${f.test_booked_date}` : null,
  ].filter(Boolean).join(" · ");

  return (
    <SectionCard testid="section-readiness" open={open} onToggle={onToggle}
      icon={<ShieldCheck className="w-5 h-5 text-indigo-600" />}
      title="Legal / Readiness"
      subtitle={subtitle || "Theory, test date, licence check"}>
      <div className="mt-3 space-y-3">
        <div className="rounded-xl bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-600 dark:text-slate-300 flex items-center gap-2">
          <span className={`inline-block w-2 h-2 rounded-full ${statusDot}`} />
          <span>Licence check: <span className="font-extrabold capitalize">{f.licence_check_status || "none"}</span></span>
          {f.licence_expiry && <span className="ml-auto text-slate-400">exp {f.licence_expiry}</span>}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
          <input
            data-testid="readiness-theory-passed"
            type="checkbox"
            checked={!!f.theory_passed}
            onChange={(e) => setF({ ...f, theory_passed: e.target.checked })}
            className="w-4 h-4"
          />
          Theory test passed
        </label>
        {f.theory_passed && (
          <Input testid="readiness-theory-date" label="Theory pass date" value={f.theory_passed_date || ""} onChange={(v) => setF({ ...f, theory_passed_date: v })} placeholder="YYYY-MM-DD" />
        )}
        <Input testid="readiness-test-date" label="Test booked date" value={f.test_booked_date || ""} onChange={(v) => setF({ ...f, test_booked_date: v })} placeholder="YYYY-MM-DD" />
        <Input testid="readiness-test-centre" label="Test centre" value={f.test_centre || ""} onChange={(v) => setF({ ...f, test_centre: v })} placeholder="e.g. Reading" />
        <TestResultHistory />
        <button data-testid="readiness-save" onClick={save} disabled={saving}
          className="w-full px-4 py-2.5 text-sm font-extrabold rounded-xl bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 flex items-center justify-center gap-2">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </SectionCard>
  );
}

/* ───────── 4. Progress (read-only, auto-aggregated) ───────── */
function ProgressSection({ open, onToggle, data }) {
  const subtitle = `${data.theory_percent}% theory · ${data.practical_lessons_count} practical lessons`;
  return (
    <SectionCard testid="section-progress" open={open} onToggle={onToggle}
      icon={<TrendingUp className="w-5 h-5 text-purple-600" />}
      title="Progress"
      subtitle={subtitle}>
      <div className="mt-3 space-y-3 text-sm">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
            <div className="h-full bg-purple-600" style={{ width: `${data.theory_percent}%` }} />
          </div>
          <span className="text-xs font-bold text-slate-700 dark:text-slate-300">{data.theory_percent}%</span>
        </div>
        <p className="text-[11px] text-slate-500">
          {data.theory_lessons_completed}/{data.theory_lessons_total} theory lessons · {data.practical_lessons_count} practical lessons logged
        </p>

        <TagList testid="progress-strengths" title="Strengths" items={data.strengths} tone="emerald" emptyLabel="Building up" />
        <TagList testid="progress-gaps" title="Priority gaps" items={data.priority_gaps} tone="amber" emptyLabel="None right now" />

        {/* Stage certs (Phase 1 — auto-issued on instructor 'Ready' verdict) */}
        <div data-testid="passport-stage-certs">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500 mb-1.5">
            Stage certificates
          </p>
          {(data.stage_certs && data.stage_certs.length > 0) ? (
            <ul className="space-y-1.5">
              {data.stage_certs.map((c) => (
                <li
                  key={c.id || c.stage_slug}
                  data-testid={`passport-stage-cert-${c.stage_slug}`}
                  className="flex items-center gap-2 text-[12px] rounded-lg bg-emerald-50 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-900/40 px-2.5 py-1.5"
                >
                  <span className="inline-flex w-5 h-5 rounded-full bg-emerald-500 text-white items-center justify-center text-[10px] font-extrabold flex-none">
                    ✓
                  </span>
                  <span className="font-extrabold text-emerald-900 dark:text-emerald-100 flex-1 min-w-0 truncate">
                    {c.stage_label || c.stage_slug}
                  </span>
                  {c.issued_at && (
                    <span className="text-[10px] text-emerald-700 dark:text-emerald-300 flex-none">
                      {String(c.issued_at).slice(0, 10)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[12px] text-slate-400 italic">
              Each stage you confirm with your instructor adds a cert here.
            </p>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

/* ───────── 5. Confidence / Style ───────── */
function ConfidenceSection({ truth, open, onToggle, initial, onSaved }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [triggerInput, setTriggerInput] = useState("");
  useEffect(() => { setF(initial); setTriggerInput(""); }, [initial]);

  const addTrigger = () => {
    if (!triggerInput.trim()) return;
    const arr = Array.from(new Set([...(f.nervous_triggers || []), triggerInput.trim()]));
    setF({ ...f, nervous_triggers: arr.slice(0, 10) });
    setTriggerInput("");
  };
  const removeTrigger = (t) => setF({ ...f, nervous_triggers: (f.nervous_triggers || []).filter((x) => x !== t) });

  const save = async () => {
    setSaving(true);
    try {
      await truth.run(
        async () => {
          const { data } = await axios.put(`${API}/api/passport/profile/learning-style`, {
            nervous_triggers: f.nervous_triggers || [],
            preferred_teaching_style: f.preferred_teaching_style || null,
            reasonable_adjustments: f.reasonable_adjustments || null,
          }, { withCredentials: true });
          onSaved(data.sections.confidence);
          onToggle();
        },
        {
          completedMessage: "Style saved.",
          failedMessage:    formatApiError(null, "Couldn't save."),
        },
      );
    } catch { /* truth.run captured */ }
    setSaving(false);
  };

  const subtitle = f.preferred_teaching_style
    ? `Prefers ${f.preferred_teaching_style}${(f.nervous_triggers || []).length ? ` · ${f.nervous_triggers.length} triggers` : ""}`
    : "Nervous triggers, style preference, adjustments";

  return (
    <SectionCard testid="section-confidence" open={open} onToggle={onToggle}
      icon={<Heart className="w-5 h-5 text-rose-600" />}
      title="Confidence & Learning Style"
      subtitle={subtitle}>
      <div className="mt-3 space-y-3">
        <div>
          <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">Preferred teaching style</label>
          <div className="mt-1 grid grid-cols-3 gap-1.5">
            {["calm", "firm", "encouraging", "structured", "chatty"].map((opt) => (
              <button key={opt}
                data-testid={`confidence-style-${opt}`}
                onClick={() => setF({ ...f, preferred_teaching_style: f.preferred_teaching_style === opt ? null : opt })}
                className={`px-2 py-1.5 text-[11px] font-bold rounded-lg border capitalize ${
                  f.preferred_teaching_style === opt
                    ? "bg-rose-600 text-white border-rose-600"
                    : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700"
                }`}
              >{opt}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">Nervous triggers</label>
          <div className="flex flex-wrap gap-1 mt-1">
            {(f.nervous_triggers || []).map((t) => (
              <span key={t} data-testid={`confidence-trigger-${t}`} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-800 dark:text-rose-300">
                {t}
                <button onClick={() => removeTrigger(t)} className="ml-1 hover:text-rose-900"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <input
              data-testid="confidence-trigger-input"
              value={triggerInput}
              onChange={(e) => setTriggerInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTrigger())}
              placeholder="e.g. roundabouts"
              className="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white"
            />
            <button onClick={addTrigger} className="px-3 py-2 text-xs font-bold rounded-lg bg-slate-900 text-white hover:bg-slate-800">Add</button>
          </div>
        </div>
        {f.mindset_trends && f.mindset_trends.length > 0 && (
          <div className="rounded-xl bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 p-3">
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500 mb-1">Recent mindset check-ins</p>
            <div className="flex flex-wrap gap-1">
              {f.mindset_trends.map((m) => (
                <span key={m.state} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
                  {m.state.replace(/_/g, " ")} · {m.count}
                </span>
              ))}
            </div>
          </div>
        )}
        <Textarea testid="confidence-adjustments" label="Reasonable adjustments" value={f.reasonable_adjustments || ""} onChange={(v) => setF({ ...f, reasonable_adjustments: v })} placeholder="Anything an instructor should know to help you learn best?" />
        <button data-testid="confidence-save" onClick={save} disabled={saving}
          className="w-full px-4 py-2.5 text-sm font-extrabold rounded-xl bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 flex items-center justify-center gap-2">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </SectionCard>
  );
}

/* ───────── 6. Instructor Notes (read-only to learner) ───────── */
function NotesSection({ open, onToggle, data }) {
  const all = [...(data.practical_notes || []), ...(data.handover_notes || [])].sort((a, b) =>
    (b.created_at || "").localeCompare(a.created_at || "")
  );
  const subtitle = all.length === 0 ? "No notes yet" : `${all.length} note${all.length === 1 ? "" : "s"} from your instructor`;
  return (
    <SectionCard testid="section-notes" open={open} onToggle={onToggle}
      icon={<StickyNote className="w-5 h-5 text-amber-600" />}
      title="Instructor Notes"
      subtitle={subtitle}>
      {all.length === 0 ? (
        <p className="mt-3 text-xs text-slate-500">Your instructor can leave notes here. They're read-only for you — always visible, never hidden.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {all.map((n) => (
            <li key={n.id} data-testid={`note-${n.id}`} className="rounded-xl bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                  n.kind === "handover" ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300" : "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
                }`}>{n.kind}</span>
                <span className="text-[10px] text-slate-400">{n.instructor_name || "Instructor"} · {new Date(n.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-xs text-slate-700 dark:text-slate-300">{n.text}</p>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

/* ───────── Small UI bits ───────── */
function Input({ testid, label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">{label}</label>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white"
      />
    </div>
  );
}

function Textarea({ testid, label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">{label}</label>
      <textarea
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white resize-none"
      />
    </div>
  );
}

function Readonly({ label, value, small }) {
  return (
    <div>
      <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`mt-0.5 ${small ? "text-xs text-slate-400" : "text-sm text-slate-700 dark:text-slate-200"}`}>{value || "—"}</p>
    </div>
  );
}

function TagList({ testid, title, items, tone, emptyLabel }) {
  const tones = {
    emerald: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
    amber: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  };
  return (
    <div data-testid={testid}>
      <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">{title}</p>
      {items && items.length > 0 ? (
        <div className="flex flex-wrap gap-1 mt-1">
          {items.map((t) => (
            <span key={t} className={`text-[11px] px-2 py-0.5 rounded-full ${tones[tone]}`}>{t}</span>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-slate-400 mt-1">{emptyLabel}</p>
      )}
    </div>
  );
}


/* ───────── Test result history (private: shown only to learner + linked instructor) ───────── */
function TestResultHistory() {
  const [state, setState] = useState({ loading: true, items: [], summary: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Use "me" — server figures it out from the session cookie. We use the
        // /me route on the profile itself so the learner_id is the logged-in user.
        const meRes = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
        const learnerId = meRes.data?.id || meRes.data?._id;
        if (!learnerId) throw new Error("no id");
        const { data } = await axios.get(`${API}/api/test-results/for-learner/${learnerId}`, { withCredentials: true });
        if (!cancelled) setState({ loading: false, items: data.items || [], summary: data.summary || null });
      } catch {
        if (!cancelled) setState({ loading: false, items: [], summary: null });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state.loading) return null;
  if (!state.items.length) {
    return (
      <div data-testid="test-result-history-empty" className="rounded-xl bg-slate-50 dark:bg-slate-900/40 border border-dashed border-slate-200 dark:border-slate-700 p-3 text-[11px] text-slate-500">
        No test attempts recorded yet.
      </div>
    );
  }
  const latest = state.items[0];
  const needsRecovery = latest?.outcome === "not_passed";
  const labelFor = (o) => ({
    passed: "Passed",
    not_passed: "Did not pass",
    cancelled_moved: "Cancelled / moved",
    not_recorded_yet: "Not recorded yet",
  }[o] || o);
  const toneFor = (o) => ({
    passed: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200",
    not_passed: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200",
    cancelled_moved: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    not_recorded_yet: "bg-slate-50 text-slate-500 dark:bg-slate-800/60 dark:text-slate-400",
  }[o] || "bg-slate-100 text-slate-700");

  return (
    <div data-testid="test-result-history" className="rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Test attempts (private)</p>
        {state.summary && (
          <p className="text-[10px] text-slate-500">
            {state.summary.passes}/{state.summary.total_attempts} passed
          </p>
        )}
      </div>
      <ul className="space-y-1.5">
        {state.items.map((it, idx) => (
          <li key={idx} className="flex items-center gap-2 text-[11px]">
            <span className={`inline-block px-2 py-0.5 rounded-full font-extrabold ${toneFor(it.outcome)}`}>
              {labelFor(it.outcome)}
            </span>
            <span className="text-slate-600 dark:text-slate-400">{it.test_date}</span>
            {it.test_centre && <span className="text-slate-400">· {it.test_centre}</span>}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[10px] text-slate-400 leading-snug">
        Private: only you and your linked instructor see this. Only passes show publicly on your instructor's Professional Passport.
      </p>
      {needsRecovery && (
        <div data-testid="test-result-recovery-prompt" className="mt-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 p-2">
          <p className="text-[11px] font-extrabold text-amber-800 dark:text-amber-200">Time for a recovery plan</p>
          <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-0.5">
            It's okay — most learners retake. Build any wobbly skills back up before your next attempt.
          </p>
          <a
            href="/learn/progress"
            data-testid="test-result-recovery-link"
            className="mt-1 inline-block text-[11px] font-extrabold text-amber-900 dark:text-amber-100 underline"
          >
            Open my progress tracker →
          </a>
        </div>
      )}
    </div>
  );
}
